import React from 'react';
import {
  Box,
  Typography,
  Container,
  Chip,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, CalendarToday, Person, AccessTime } from '@mui/icons-material';

const WhatIsZiweiScreen: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleBackToBlog = () => {
    navigate('/blog');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="md">
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToBlog}
            sx={{
              color: 'white',
              mb: 3,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            返回博客
          </Button>
          
          <Chip
            label="占星学"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              mb: 2,
            }}
          />
          
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 'bold',
              mb: 3,
              lineHeight: 1.2,
            }}
          >
            什么是紫微斗数？
          </Typography>
          
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              flexWrap: 'wrap',
              opacity: 0.9,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Person sx={{ fontSize: 18 }} />
        <Typography variant="body2">紫微AI团队</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarToday sx={{ fontSize: 18 }} />
              <Typography variant="body2">January 15, 2025</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 18 }} />
              <Typography variant="body2">5 min read</Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Article Content */}
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            bgcolor: 'background.paper',
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: 'primary.main',
              fontWeight: 'medium',
              mb: 3,
            }}
          >
            紫微斗数简介
          </Typography>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 3 }}>
            紫微斗数是中国古代占星学的重要分支，被誉为"帝王学"。它以紫微星为主星，结合其他108颗星曜，通过复杂的排盘系统来预测人的命运和性格特征。
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
            历史渊源
          </Typography>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 3 }}>
            紫微斗数起源于宋代，由道士陈抟所创。经过千年的发展和完善，形成了完整的理论体系。它融合了阴阳五行、天干地支等传统文化元素。
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
            基本构成
          </Typography>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 3 }}>
            紫微斗数命盘由十二宫位组成，每个宫位都有其特定的含义和作用。通过分析星曜在各宫位的分布和相互关系，可以了解一个人的性格、事业、感情等各个方面。
          </Typography>

          <Box
            component="ul"
            sx={{
              pl: 3,
              mb: 3,
              '& li': {
                mb: 1,
                lineHeight: 1.6,
              },
            }}
          >
            <li>
              <Typography variant="body1">
                <strong>十二宫位:</strong> 命宫、兄弟宫、夫妻宫、子女宫、财帛宫、疾厄宫、迁移宫、奴仆宫、官禄宫、田宅宫、福德宫、父母宫
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>主要星曜:</strong> 紫微、天机、太阳、武曲、天同、廉贞、天府、太阴、贪狼、巨门、天相、天梁、七杀、破军
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>五行属性:</strong> 金、木、水、火、土五种属性，影响星曜的特性和相互关系
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                <strong>大限流年:</strong> 十年一大限，一年一流年，用于分析不同时期的运势变化
              </Typography>
            </li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
            现代应用
          </Typography>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 3 }}>
            在现代社会，紫微斗数不仅用于个人命理分析，还广泛应用于职业规划、人际关系、投资理财等方面。结合现代心理学和统计学方法，使其更具科学性和实用性。
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
            与西方占星的区别
          </Typography>

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 3 }}>
            与西方占星学相比，紫微斗数更注重时间的精确性和命运的细节分析。它不仅考虑出生时间，还结合中国传统的天干地支系统，提供更为详细和个性化的分析结果。
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              p: 3,
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              准备好探索了吗？
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
              立即开始您的紫微斗数之旅，发现属于您的命运密码
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
            >
              开始使用
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default WhatIsZiweiScreen;